import React, { useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import { PlatformHeader } from '@doer/frontend-components/platform-header';
import { useEditorContext } from './context/editor-context';
import { EditorProvider } from './context/editor-provider';
import { useAuthContext } from '../../context/auth';
import { StudioLayout } from './components/layout/studio-layout';
import { HistoryDrawer } from './components/layout/history-drawer';
import { EditorPanel } from './components/editor/editor-panel';
import {
  SceneListPanel,
  SCENE_PANEL_WIDTH_EXPANDED,
  SCENE_PANEL_WIDTH_COLLAPSED
} from './components/scene-list/scene-list-panel';
import { SettingsPanel } from './components/settings/settings-panel';
import { ProjectProgressModal } from './components/progress/project-progress-modal';
import { StatusTag } from 'src/components/ui/status-tag';
import { ClientLogo } from 'src/components/ui/client-logo';
import { PLATFORM_MODULES } from '../../constants/platform-modules';
import { getClientModules } from 'src/clients/client-config';
import { getClientLabel } from 'src/clients/client-options';
import type { ClientID } from '../../types/banner/clients/clients';
import { C } from './constants/design-tokens';

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  processing: { label: 'Generando escenas...', color: C.warning },
  completed: { label: 'Proyecto listo', color: C.success },
  partial: { label: 'Completado parcialmente', color: C.warning },
  failed: { label: 'Error al generar', color: C.danger }
};

const StudioShell: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userInfo, signOut } = useAuthContext();
  const clientId = userInfo?.['custom:clientId'];
  const {
    form,
    projectId,
    projectStatus,
    projectScenes,
    projectTotalScenes,
    projectCompletedScenes,
    activeIndex,
    setActiveIndex,
    addScene,
    scenePanelCollapsed,
    toggleScenePanel,
    historyOpen,
    setHistoryOpen,
    onRegenerate,
    loadProjectForEdit,
    progressModalOpen,
    closeProgressModal
  } = useEditorContext();

  const handleEdit = useCallback(() => {
    if (projectId) navigate(`/avatars-editor/${projectId}`);
  }, [projectId, navigate]);

  // Entrada al edit mode desde el botón "Back" del editor. Levantamos el id,
  // disparamos la carga del proyecto y limpiamos la URL para que un refresh no re-dispare el load.
  const editProjectId = searchParams.get('projectId');
  useEffect(() => {
    if (!editProjectId) return;
    loadProjectForEdit(editProjectId);
    navigate('/avatar-video', { replace: true });
  }, [editProjectId, loadProjectForEdit, navigate]);

  const allowedModuleIds = getClientModules(clientId);
  const modules = PLATFORM_MODULES.filter(m => allowedModuleIds.includes(m.id));

  const studioActions = (
    <>
      {projectStatus !== 'idle' && STATUS_CONFIG[projectStatus] && (
        <StatusTag label={STATUS_CONFIG[projectStatus].label} size='small' />
      )}
      <Button type='text' icon={<HistoryOutlined />} onClick={() => setHistoryOpen(true)} style={{ color: '#565C76' }}>
        Historial
      </Button>
    </>
  );

  return (
    <>
      <StudioLayout
        topbar={
          <PlatformHeader
            clientLogo={<ClientLogo clientId={clientId} />}
            modules={modules}
            activeModule='digital-brand-ambassador'
            extraActions={studioActions}
            onModuleChange={moduleId => {
              const mod = PLATFORM_MODULES.find(m => m.id === moduleId);
              if (mod) navigate(mod.path);
            }}
            onSignOut={signOut}
            userName={clientId ? getClientLabel(clientId as ClientID) : undefined}
            userEmail={userInfo?.email}
            environment={process.env.REACT_APP_ENV_SUFFIX === 'alt' ? 'alt' : undefined}
          />
        }
        leftWidth={scenePanelCollapsed ? SCENE_PANEL_WIDTH_COLLAPSED : SCENE_PANEL_WIDTH_EXPANDED}
        left={
          <SceneListPanel
            scenes={form.scenes}
            activeIndex={activeIndex}
            projectScenes={projectScenes}
            onSelectScene={setActiveIndex}
            onAddScene={addScene}
            collapsed={scenePanelCollapsed}
            onToggleCollapse={toggleScenePanel}
          />
        }
        center={<EditorPanel />}
        right={<SettingsPanel />}
      />

      <HistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onEdit={id => navigate(`/avatars-editor/${id}`)}
        onRegenerate={onRegenerate}
      />
      <ProjectProgressModal
        open={progressModalOpen}
        projectStatus={projectStatus}
        projectScenes={projectScenes}
        totalScenes={projectTotalScenes}
        completedScenes={projectCompletedScenes}
        onClose={closeProgressModal}
        onEdit={handleEdit}
      />
    </>
  );
};

export const AvatarsPage: React.FC = () => (
  <EditorProvider>
    <StudioShell />
  </EditorProvider>
);
